// Ruta: packages/shared/src/telemetry/fetchWithTelemetry.ts
// Wrapper around fetch that tracks API call duration and failures.
//
// Usage:
//   import { patchFetchWithTelemetry } from '@heyshaker/shared'
//   patchFetchWithTelemetry()  // Call once at app startup
//
// Or use the wrapper directly:
//   import { fetchWithTelemetry } from '@heyshaker/shared'
//   const response = await fetchWithTelemetry('/api/v1/stores')
//
// HAL-FE-002

import { telemetry } from './telemetry.service.js'

/**
 * A fetch wrapper that records request duration, status, and errors
 * via the telemetry service.
 */
export async function fetchWithTelemetry(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
  const method = init?.method ?? 'GET'
  const start = performance.now()

  try {
    const response = await fetch(input, init)
    const durationMs = Math.round(performance.now() - start)

    telemetry.trackApiCall({
      method,
      url: _sanitizeUrl(url),
      status: response.status,
      durationMs,
      ok: response.ok,
      errorCode: response.ok ? undefined : `HTTP_${response.status}`,
    })

    return response
  } catch (error) {
    const durationMs = Math.round(performance.now() - start)

    telemetry.trackApiCall({
      method,
      url: _sanitizeUrl(url),
      status: 0,
      durationMs,
      ok: false,
      errorCode: 'NETWORK_ERROR',
    })

    throw error
  }
}

/**
 * Monkey-patches the global `fetch` to automatically track all API calls.
 * Call once at app startup. Safe to call multiple times (idempotent).
 */
let _patched = false

export function patchFetchWithTelemetry(): void {
  if (_patched || typeof globalThis.fetch !== 'function') return
  _patched = true

  const originalFetch = globalThis.fetch.bind(globalThis)

  globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
    const method = init?.method ?? 'GET'

    // Skip telemetry endpoint to avoid infinite recursion
    if (_isTelemetryEndpoint(url)) {
      return originalFetch(input, init)
    }

    const start = performance.now()

    try {
      const response = await originalFetch(input, init)
      const durationMs = Math.round(performance.now() - start)

      telemetry.trackApiCall({
        method,
        url: _sanitizeUrl(url),
        status: response.status,
        durationMs,
        ok: response.ok,
        errorCode: response.ok ? undefined : `HTTP_${response.status}`,
      })

      return response
    } catch (error) {
      const durationMs = Math.round(performance.now() - start)

      telemetry.trackApiCall({
        method,
        url: _sanitizeUrl(url),
        status: 0,
        durationMs,
        ok: false,
        errorCode: 'NETWORK_ERROR',
      })

      throw error
    }
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Remove query params and hash to avoid logging sensitive data */
function _sanitizeUrl(url: string): string {
  try {
    const u = new URL(url, window.location.origin)
    return `${u.pathname}`
  } catch {
    return url.split('?')[0]?.split('#')[0] ?? url
  }
}

/** Check if a URL points to the telemetry endpoint */
function _isTelemetryEndpoint(url: string): boolean {
  return url.includes('/telemetry')
}
